import {
  CreateDateColumn,
  UpdateDateColumn,
  Entity,
  Index,
  Column,
  RelationId,
  ManyToOne,
  PrimaryGeneratedColumn
} from 'typeorm'
import { ObjectType, Field, Int, ID, registerEnumType } from 'type-graphql'

import { Domain, ScalarObject } from '@things-factory/shell'
import { User } from '@things-factory/auth-base'
import { config } from '@things-factory/env'

const ORMCONFIG = config.get('ormconfig', {})
const DATABASE_TYPE = ORMCONFIG.type

export enum BoardTemplateStatus {
  DRAFT = 'DRAFT',
  RELEASED = 'RELEASED'
}

registerEnumType(BoardTemplateStatus, {
  name: 'BoardTemplateStatus',
  description: 'The publication status of a board template, indicating whether it is a draft or released.'
})

@Entity()
@Index('ix_board_template_0', (boardTemplate: BoardTemplate) => [boardTemplate.domain, boardTemplate.name], {
  unique: true
})
@ObjectType({ description: 'A reusable template for creating new boards.' })
export class BoardTemplate {
  @PrimaryGeneratedColumn('uuid')
  @Field(type => ID, { description: 'Unique identifier for the board template.' })
  readonly id: string

  @ManyToOne(type => Domain)
  @Field(type => Domain, { description: 'The domain to which this template belongs.' })
  domain?: Domain

  @RelationId((boardTemplate: BoardTemplate) => boardTemplate.domain)
  domainId?: string

  @Column()
  @Field({ nullable: true, description: 'The name of the board template.' })
  name?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'A detailed description of the board template.' })
  description?: string

  @Column('simple-json', { nullable: true, default: null })
  @Field(type => [String], { nullable: true, description: 'A list of tags for categorizing the template.' })
  tags?: string[]

  @Column({ nullable: true, default: 'private' })
  @Field({ nullable: true, description: "The visibility of the template: 'private', 'public', or 'domain'." })
  visibility?: string // 'private' | 'public' | 'domain'

  @Column({
    nullable: true,
    type:
      DATABASE_TYPE == 'mysql' || DATABASE_TYPE == 'mariadb'
        ? 'longtext'
        : DATABASE_TYPE == 'oracle'
          ? 'clob'
          : DATABASE_TYPE == 'mssql'
            ? 'nvarchar'
            : 'varchar',
    length: DATABASE_TYPE == 'mssql' ? 'MAX' : undefined
  })
  @Field({ nullable: true, description: 'The JSON model that defines the layout and components of the template.' })
  model?: string

  @Column({
    nullable: true,
    type:
      DATABASE_TYPE == 'mysql' || DATABASE_TYPE == 'mariadb'
        ? 'longtext'
        : DATABASE_TYPE == 'oracle'
          ? 'clob'
          : DATABASE_TYPE == 'mssql'
            ? 'nvarchar'
            : 'varchar',
    length: DATABASE_TYPE == 'mssql' ? 'MAX' : undefined
  })
  @Field({ nullable: true, description: 'A base64 encoded thumbnail image of the template.' })
  thumbnail?: string

  @CreateDateColumn()
  @Field({ nullable: true, description: 'The timestamp when the template was created.' })
  createdAt?: Date

  @UpdateDateColumn()
  @Field({ nullable: true, description: 'The timestamp when the template was last updated.' })
  updatedAt?: Date

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true, description: 'The user who created the template.' })
  creator?: User

  @RelationId((boardTemplate: BoardTemplate) => boardTemplate.creator)
  creatorId?: string

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true, description: 'The user who last updated the template.' })
  updater?: User

  @RelationId((boardTemplate: BoardTemplate) => boardTemplate.updater)
  updaterId?: string
}
